import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Button } from 'reactstrap';

import { FileDisplay, FileUpload } from '@techchange/filestack';
import { messages } from '../../../_shared/i18n/messages';

const MAX_NUMBER_OF_FILES = 1;

const propTypes = {

	/**
	 * The input id. If none is provided, the input name is used.
	 */
	id: PropTypes.string,

	/**
	 * The input name.
	 */
	name: PropTypes.string.isRequired,

	/**
	 * The input value, i.e the file URL.
	 */
	value: PropTypes.string,

	/**
	 * True, if editing the input value.
	 */
	isEditing: PropTypes.bool,

	/**
	 * True, if the input value is invalid.
	 */
	isInvalid: PropTypes.bool,

	/**
	 * True, if the user is the previewing the the form.
	 */
	isPreview: PropTypes.bool,

	/**
	 * The type of files to allow for upload.
	 */
	accept: PropTypes.arrayOf(PropTypes.string),

	/**
	 * The visibility settings of the file(s) to upload.
	 */
	visibility: PropTypes.oneOf(['public', 'private']),

	/**
	 * The settings for filestack (required when editing!).
	 */
	filestack: PropTypes.shape({

		// The API key
		apiKey: PropTypes.string,

		// The security policy and signature
		security: PropTypes.shape({

			policy: PropTypes.string,

			signature: PropTypes.string,

		}),

		// Flag indicating if mode is production
		production: PropTypes.bool,

		// The path
		path: PropTypes.string,

		// The container
		container: PropTypes.string,

		// The region
		region: PropTypes.string,

	}),

	/**
	 * Callback function to handle changes to the input value.
	 */
	onChange: PropTypes.func,

};

const FileInput = (props) => {

	const {
		id,
		name,
		value,
		isEditing,
		isInvalid,
		isPreview,
		accept,
		visibility,
		filestack,
		onChange
	} = props;

	if (!isEditing) {
		return (
			<div>
				{value
					? <FileDisplay url={value} />
					: <span className="tc-material-input-display-empty-value">
						<FormattedMessage {...messages.TC_COMPONENTS_FORM_INPUT_TEXT_EMPTY_VALUE} />
					</span>
				}
			</div>
		);
	}

	const onError = (error) => console.error(error);

	const onUpload = (fileUrls) => {

		if (fileUrls && fileUrls.length === MAX_NUMBER_OF_FILES) {

			const url = fileUrls[0];

			onChange(url);

		}

	};

	return (
		<div
			id={id || name}
			className={`tc-material-input-file-upload ${isInvalid ? 'tc-material-input-invalid' : ''}`}>
			{!value &&
				<FileUpload
					accept={accept}
					maxFiles={MAX_NUMBER_OF_FILES}
					visibility={visibility}
					filestack={filestack}
					onError={onError}
					onUpload={onUpload}>
					<Button
						color="neutral"
						size="sm"
						type="button"
						outline>
						<FormattedMessage {...messages.TC_COMPONENTS_FORM_INPUT_FILE_UPLOAD_BUTTON_TEXT} />
					</Button>
				</FileUpload>
			}
			{/* Render dummy upload button for preview purposes */}
			{!value && !filestack && isPreview &&
				<Button
					color="neutral"
					size="sm"
					type="button"
					outline>
					<FormattedMessage {...messages.TC_COMPONENTS_FORM_INPUT_FILE_UPLOAD_BUTTON_TEXT} />
				</Button>
			}
			{value &&
				<FileDisplay
					url={value}
					onRemove={() => onChange('')} />
			}
		</div>
	);

};

FileInput.propTypes = propTypes;

export default FileInput;
